/**
 * Users domain — React Query hooks.
 *
 * Read hooks are re-exported from the generated client; `useUsers` is a
 * hand-written wrapper preserving the legacy `enabled: !isLimited` +
 * `refetchOnMount: false` behavior. Write hooks go through
 * `useInvalidatingMutation`.
 */

import { useContext, useMemo } from 'react'
import { useQuery, type UseQueryOptions } from '@tanstack/react-query'

import { AuthContext } from '../../context/authentication/auth-context'
import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'
import type { AppUser as User } from '../generated/types/AppUser'

// ── Read hook re-exports ───────────────────────────────────────────
export {
  useListUserTypes,
  listUserTypesQueryKey,
  useListInvitations,
  listInvitationsQueryKey,
  listUsersQueryKey,
} from '../generated/hooks/usersController'

// ── Internal imports for wrappers ──────────────────────────────────
import { listUsersQueryKey as _listUsersQueryKey } from '../generated/hooks/usersController'
import { listUsers } from '../generated/clients/listUsers'
import { updateUserType } from '../generated/clients/updateUserType'
import { inviteUser } from '../generated/clients/inviteUser'
import { resendInvitation } from '../generated/clients/resendInvitation'
import { deleteInvitation } from '../generated/clients/deleteInvitation'

import type { ListUsersQueryParams } from '../generated/types/ListUsers'
import type { InviteUserMutationRequest } from '../generated/types/InviteUser'
import type {
  UpdateUserTypeMutationRequest,
  UpdateUserTypePathParams,
} from '../generated/types/UpdateUserType'
import type { ResendInvitationPathParams } from '../generated/types/ResendInvitation'
import type { DeleteInvitationPathParams } from '../generated/types/DeleteInvitation'

export type {
  InviteUserMutationRequest,
  UpdateUserTypeMutationRequest,
  ListUsersQueryParams,
}

// ── Hand-written read wrappers ─────────────────────────────────────

/**
 * List company users. Replaces `api.useGetUsers`. Preserves the legacy
 * defaults: skip fetching for Limited users and `refetchOnMount: false`.
 * Pass `enabled` to override the isLimited gate.
 */
export function useUsers(
  params?: ListUsersQueryParams,
  options: {
    query?: Omit<UseQueryOptions<User[], Error>, 'queryKey' | 'queryFn'>
  } = {}
) {
  const { isLimited } = useContext(AuthContext)
  const { query = {} } = options
  return useQuery<User[], Error>({
    queryKey: _listUsersQueryKey(params),
    queryFn: ({ signal }) => listUsers(params, { signal }),
    enabled: !isLimited,
    refetchOnMount: false,
    ...query,
  })
}

/** Active Standard+Admin users (RSM-eligible). Replaces `api.getRSMEligibleUsers`. */
export function useRSMEligibleUsers(
  options: {
    query?: Omit<UseQueryOptions<User[], Error>, 'queryKey' | 'queryFn'>
  } = {}
) {
  return useUsers(
    { user_type: ['Standard', 'Admin'], is_active: 'true' },
    options
  )
}

/** Derived `{label,value,original}` options. Replaces `api.useGetUserOptions`. */
export function useUserOptions({
  withNoneOption,
  onlyActive,
}: {
  withNoneOption?: boolean
  onlyActive?: boolean
}) {
  const { data: users } = useUsers()
  const opts = useMemo(() => {
    if (!users) return undefined
    const out: { label: string; value: number; original?: User }[] = users
      .filter((user) => (onlyActive ? user.is_active : true))
      .map((user) => ({
        label: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
        value: user.id,
        original: user,
      }))
    if (withNoneOption) out.unshift({ label: 'No User', value: -1 })
    return out
  }, [users, withNoneOption, onlyActive])
  return { data: opts, isLoading: !users, isError: false }
}

// ── Mutation hooks (auto-invalidation) ─────────────────────────────

export function useUpdateUserType() {
  return useInvalidatingMutation('updateUserType', invalidationRegistry, {
    mutationFn: (variables: {
      user_id: UpdateUserTypePathParams['user_id']
      data: UpdateUserTypeMutationRequest
    }) => updateUserType({ user_id: variables.user_id }, variables.data),
  })
}

export function useInviteUser() {
  return useInvalidatingMutation('inviteUser', invalidationRegistry, {
    mutationFn: (variables: { data: InviteUserMutationRequest }) =>
      inviteUser(variables.data),
  })
}

export function useResendInvitation() {
  return useInvalidatingMutation('resendInvitation', invalidationRegistry, {
    mutationFn: (variables: { id: ResendInvitationPathParams['id'] }) =>
      resendInvitation({ id: variables.id }),
  })
}

export function useDeleteInvitation() {
  return useInvalidatingMutation('deleteInvitation', invalidationRegistry, {
    mutationFn: (variables: { id: DeleteInvitationPathParams['id'] }) =>
      deleteInvitation({ id: variables.id }),
  })
}
